import React, { useState, useEffect, useContext, useMemo } from 'react';
import styled from 'styled-components';
import { Prompt } from 'react-router';
import _ from 'lodash';
import { AuthContext } from 'contexts';
import {
  buildFullName,
  sortUserName,
  featureEnabled,
  isAuthForLocation,
} from 'utils';
import {
  getSupraLockboxGrantees,
  updateSupraLockboxGrantees,
  ParsedSupraLockboxJsonType,
} from 'api/SupraLockboxController';
import { getOrgUsers, searchForMLSStaff, find } from 'api/UserController';

import errorReporter from '@app/errorReporter';
import {
  SupraSystemSubscriptionStatus,
  SupraSystemSubscriptionType,
} from '@bb/domain/dist/types';
import {
  ROLES,
  isAgent,
  isBranchAdmin,
  isGlobalAdmin,
  isOwner,
  isManager,
  isTeamAdmin,
} from '@bb/users';
import type {
  IUserOrgResponse,
  IUserSearchForMLSStaffResponse,
} from '@/app-package';

import { Dropdown } from 'antd';
import { Button, Icon, Text, Modal, Table, Tooltip, message } from 'components';

import { ColumnsType } from 'antd/es/table';
import { useBreakpoint } from 'hooks';
import UserSelectModal from './UserSelectModal';
import ActionBar from './ActionBar';

type ShacklePermissionTableProps = { lockbox: ParsedSupraLockboxJsonType };

const ShacklePermissionTable: React.FC<ShacklePermissionTableProps> = ({
  lockbox,
}) => {
  const [permittedUsers, setPermittedUsers] = useState<
    IUserOrgResponse['users']
  >([]);
  const [savedPermittedUsers, setSavedPermittedUsers] = useState<
    IUserOrgResponse['users']
  >([]);
  const [usersToUnassign, setUsersToUnassign] = useState<string[]>([]);

  const [openModal, setOpenModal] = useState(false);

  const [isDirty, setIsDirty] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);

  const { lg } = useBreakpoint();
  const { auth } = useContext(AuthContext);

  const [
    officeWideShacklePermissionsGranted,
    setOfficeWideShacklePermissionsGranted,
  ] = useState<boolean>(false);

  const isMlsUser = auth.org.type === 'mlsStaff';

  const lockboxSlowAssignmentEnhancementsEnabled = featureEnabled(
    'LockboxSlowAssignmentEnhancements',
  );

  // ATE-166 https://brokerbay.atlassian.net/browse/ATE-166
  // If limited subscriber has an admin role, remove ability to
  // change grantees for lockboxes that do not belong to them.
  const restrictLimitedUser = React.useMemo(() => {
    const isLimitedSubscriber =
      auth.supraSubscription?.type === SupraSystemSubscriptionType.LIMITED;

    const isAdminRole = ROLES[auth.role] >= ROLES.TEAM_ADMIN;

    const isOwnerOrAssignee =
      lockbox.owner === auth.id || lockbox.assignee === auth.id;

    if (isLimitedSubscriber && isAdminRole && !isOwnerOrAssignee) {
      return true;
    }
    if (isLimitedSubscriber && !isAdminRole && !isOwnerOrAssignee) {
      // In this case the user would be a grantee and won't see shackle permission tab anyway
      // adding this in case visibility changes
      return true;
    }
    return false;
  }, [auth.supraSubscription, auth.role, auth.id, lockbox]);

  useEffect(() => {
    setPermittedUsers(savedPermittedUsers);
  }, [savedPermittedUsers]);

  /**
   * Set the office where the shackle permissions will be granted, wheather to the owner or assignee's office.
   *
   * - If lockbox owner is MLS/BOR and assignee is set, then permissions will be given to users in assignee's office.
   * - If lockbox owner is Office/User, then permissions will be given to users in owner's office, regardless of assignee.
   *
   * https://honeywell.atlassian.net/browse/BET-1665
   */
  const permissionOffice = useMemo<'owner' | 'assignee' | ''>(() => {
    if (
      (lockbox.ownerType === 'mls' || lockbox.ownerType === 'board') &&
      lockbox.assignee
    ) {
      return 'assignee';
    }
    if (lockbox.ownerType === 'office' || lockbox.ownerType === 'user') {
      return 'owner';
    }
    return '';
  }, [lockbox.ownerType, lockbox.assignee]);

  /**
   * Bulk shackle permission assignment via "Add all subscribed users".
   *
   * This action is DISABLED when:
   * 1. Lockbox owner is MLS/BOR and no assignee is set.
   * 2. Global Admin/Owner/Manager is for assignee and lockbox owner is outside their org.
   * 3. Branch Admin is for assignee and lockbox owner is outside their org OR lockbox owner location is outside their authorized branch locations.
   * 4. Agent is the lockbox assignee and lockbox owner is outside their org.
   *
   * https://honeywell.atlassian.net/browse/BET-1665
   */
  const disableBulkShacklePermissions = useMemo<boolean>(() => {
    const authIsGlobalAdmin = isGlobalAdmin(auth);
    const authIsBranchAdmin = isBranchAdmin(auth);
    const authIsAgent = isAgent(auth);
    const authIsOwner = isOwner(auth);
    const authIsManager = isManager(auth);
    const authIsTeamAdmin = isTeamAdmin(auth);

    return (
      ((lockbox.ownerType === 'mls' || lockbox.ownerType === 'board') &&
        !lockbox.assignee) ||
      ((authIsGlobalAdmin || authIsOwner || authIsManager) &&
        lockbox.assigneeType === 'user' &&
        lockbox.ownerType !== 'mls' &&
        lockbox.ownerType !== 'board' &&
        lockbox.parsedAssignee?.orgId === auth?.org.id &&
        lockbox.parsedOwner?.orgId !== auth?.org.id) ||
      (authIsBranchAdmin &&
        lockbox.assigneeType === 'user' &&
        lockbox.ownerType !== 'mls' &&
        lockbox.ownerType !== 'board' &&
        lockbox.parsedAssignee?.orgId === auth?.org.id &&
        (lockbox.parsedOwner?.orgId !== auth?.org.id ||
          !isAuthForLocation(auth, {
            location:
              lockbox.ownerType === 'office'
                ? lockbox.ownerBranchIndex
                : lockbox.parsedOwner?.location,
            locations: [],
          }))) ||
      authIsTeamAdmin ||
      (authIsAgent &&
        lockbox.assigneeType === 'user' &&
        lockbox.ownerType !== 'mls' &&
        lockbox.ownerType !== 'board' &&
        lockbox.assignee === auth?.id &&
        lockbox.parsedOwner?.orgId !== auth?.org.id)
    );
  }, [lockbox, auth]);

  const shacklePermissionColumns: ColumnsType<IUserOrgResponse['users'][0]> = [
    {
      title: 'User',
      dataIndex: ['firstName', 'lastName'],
      key: 'user',
      sorter: (user1, user2) => {
        if (user1.firstName !== user2.firstName) {
          return user1.firstName.localeCompare(user2.firstName);
        }
        return user1.lastName.localeCompare(user2.lastName);
      },
      render: (name, user) => buildFullName(user.firstName, user.lastName),
    },
    {
      title: 'Member #',
      dataIndex: 'mls_numbers',
      key: 'mls_numbers',
      render: mls_numbers => (
        <MLSNumberContainer>
          {mls_numbers && mls_numbers.length
            ? mls_numbers?.map(({ board, number }: todo) => (
                <div>
                  {board}: {number}
                </div>
              ))
            : false}
        </MLSNumberContainer>
      ),
    },
    {
      title: 'Office',
      dataIndex: 'orgName',
      key: 'orgName',
      render: (orgName, user) => (
        <>
          <Text block>
            {featureEnabled('LockboxSlowAssignmentEnhancements')
              ? (user.officeName ?? user.orgName)
              : user.orgName}
          </Text>
          <Text type="secondary">
            {isMlsUser
              ? user.branchName
              : auth.org?.locations?.[Number(user.location)]?.name}
          </Text>
        </>
      ),
    },
  ];

  if (!restrictLimitedUser) {
    shacklePermissionColumns.push({
      title: 'Action',
      render: (action, user) => (
        <Button
          key={user.id}
          type="link"
          onClick={() => {
            if (
              (lockbox.ownerType === 'user' && user.id === lockbox.owner) ||
              (lockbox.assigneeType === 'user' && user.id === lockbox.assignee)
            ) {
              Modal.warning({
                title: 'Cannot remove owner or assignee',
                content:
                  'You cannot remove shackle permissions for the owner or assignee of this lockbox.',
              });
            } else {
              const { listing } = lockbox;
              const listingAddress = `${listing?.line1}${
                listing?.line2 ? ` ${listing.line2}` : ''
              }`;
              Modal.confirm({
                title: 'Unassign user?',
                content: `Unassigning this user will revoke their shackle permissions${
                  listing
                    ? `, and may remove the lockbox from listing ${listingAddress} if they are the listing agent for this listing`
                    : '. The lockbox is not currently assigned to a listing'
                }. Do you wish to continue?`,
                okText: 'Unassign',
                cancelText: 'Cancel',
                onOk() {
                  setPermittedUsers(
                    permittedUsers.filter(
                      permittedUser => permittedUser.id !== user.id,
                    ),
                  );
                  setIsDirty(true);
                },
              });
            }
          }}
        >
          Unassign
        </Button>
      ),
    });
  }

  useEffect(() => {
    setLoading(true);
    (async () => {
      if (lockbox._id) {
        const fetchedGrantees = await getSupraLockboxGrantees(
          lockbox._id,
          true,
        );
        setSavedPermittedUsers(fetchedGrantees.sort(sortUserName));
      }
      setLoading(false);
    })();
  }, [lockbox._id, lockbox.assignee, lockbox.owner]);

  const getUsersToAssign = async (userNameToSearch: string, size?: number) => {
    const supraSubscriptionStatuses = [
      SupraSystemSubscriptionStatus.subscribed,
    ];
    const excludeUsers = [
      ...permittedUsers.map(user => user.id),
      ...(lockbox.owner && lockbox.ownerType === 'user' ? [lockbox.owner] : []),
      ...(lockbox.assignee && lockbox.assigneeType === 'user'
        ? [lockbox.assignee]
        : []),
    ];

    const getOrgId = () => {
      let orgId;
      if (lockbox.assignee && lockbox.assigneeType === 'org') {
        orgId = lockbox.assignee;
      }
      if (lockbox.assignee && lockbox.assigneeType !== 'org') {
        orgId = lockbox.parsedAssignee?.orgId;
      }
      if (!lockbox.assignee && lockbox.ownerType! === 'org') {
        orgId = lockbox.owner;
      }
      if (!lockbox.assignee && lockbox.ownerType !== 'org') {
        orgId = lockbox.parsedOwner?.orgId;
      }

      /**
       * When feature is enabled, use the lockbox owner's orgId to search for subscribed users
       * during MlsStaff search.
       */
      if (
        featureEnabled('LockboxSlowAssignmentEnhancements') &&
        lockbox.ownerType !== 'mls' &&
        lockbox.ownerType !== 'board'
      ) {
        orgId = lockbox.parsedOwner?.orgId;
      }
      return orgId;
    };

    let response: IUserSearchForMLSStaffResponse | IUserOrgResponse;
    let isMlsSearchResponse = false;
    const orgId = getOrgId();

    if (isMlsUser) {
      response = await searchForMLSStaff({
        name: userNameToSearch,
        supraSubscriptionStatuses,
        eligibleGranteesOnly: true,
        excludeUsers,
        orgId,
        skipAutoCoopOrgOverride: true,
        size,
      });
      isMlsSearchResponse = true;
    } else {
      response = await getOrgUsers({
        userName: userNameToSearch,
        supraSubscriptionStatuses,
        eligibleGranteesOnly: true,
        excludeUsers,
        size,
        isShacklePermissionRequest: true,
      });
    }

    /**
     * When granting shackle permissions to all subscribed users, the fetched users should only be from the lockbox owner
     * or assignee's office, and not all users in the MLS/BOR, brokerage, or of any cooop types.
     *
     * https://honeywell.atlassian.net/browse/BET-1665
     */
    if (featureEnabled('LockboxSlowAssignmentEnhancements')) {
      let lockboxLocation: string;
      let lockboxAddress: string;

      // Find the office which the lockbox belongs to. Preference given to the owner's office if not MLS/BOR.
      if (
        lockbox.ownerType === 'user' ||
        ((lockbox.ownerType === 'mls' || lockbox.ownerType === 'board') &&
          lockbox.assigneeType === 'user')
      ) {
        lockboxLocation =
          lockbox.ownerType === 'user'
            ? lockbox.parsedOwner?.location
            : lockbox.parsedAssignee?.location;
        lockboxAddress =
          lockbox.ownerType === 'user'
            ? lockbox.parsedOwner?.address
            : lockbox.parsedAssignee?.address;
      } else if (
        lockbox.ownerType === 'office' ||
        lockbox.assigneeType === 'office'
      ) {
        lockboxLocation =
          lockbox.ownerBranchIndex ?? lockbox.assigneeBranchIndex;
        lockboxAddress =
          lockbox.ownerType === 'office'
            ? lockbox.parsedOwner.address
            : lockbox.parsedAssignee.address;
      }

      // Filter out users that do not belong to the lockbox office
      response.users = response?.users.filter(
        user =>
          user.orgId === orgId &&
          user.location === lockboxLocation &&
          user.address === lockboxAddress,
      );
    }

    // Assumes all users in the search results are from the same org as the requestor
    // This appears to be the case in userService.searchUsers()
    // If the requestor is an MLS user orgName is returned in the response
    const usersWithOrg =
      response?.users.map(user => ({
        ...user,
        ...(!isMlsUser ? { orgName: auth.org.name } : {}),
      })) || [];

    return usersWithOrg as IUserOrgResponse['users'];
  };

  const saveShackPermission = async () => {
    try {
      await updateSupraLockboxGrantees(
        +lockbox.serialNumber,
        lockbox._id,
        permittedUsers.map(user => user.id),
        officeWideShacklePermissionsGranted,
      );
      setSavedPermittedUsers(permittedUsers);
      setIsDirty(false);
      message.success('Shackle permission saved.');
    } catch (err) {
      setPermittedUsers(savedPermittedUsers);
      message.error('Shackle permission failed to save.');
    } finally {
      // After saving, reset officeWideShacklePermissionsGranted
      setOfficeWideShacklePermissionsGranted(false);
    }
  };

  const shouldDisableShacklePermissions = () => {
    // MLS staff can assign shackle permissions when owner is MLS/BOR and no assignee is set
    if (
      featureEnabled('LockboxSlowAssignmentEnhancements') &&
      isMlsUser &&
      (lockbox.ownerType === 'mls' || lockbox.ownerType === 'board') &&
      !lockbox.assignee
    )
      return false;
    if (
      (lockbox.ownerType === 'mls' || lockbox.ownerType === 'board') &&
      !lockbox.assignee
    ) {
      return true;
    }
    return false;
  };

  const addAllSubscribedUsers = async () => {
    let usersWithOrg: IUserOrgResponse['users'] = [];
    try {
      setLoading(true);
      // soft limit on fetching all subscribed users
      usersWithOrg = await getUsersToAssign('', 10000);
    } catch (err) {
      errorReporter.error(err);
    } finally {
      setPermittedUsers(users =>
        _.unionBy(users, usersWithOrg, 'id').sort(sortUserName),
      );
      setLoading(false);
      if (
        featureEnabled('LockboxSlowAssignmentEnhancements') &&
        usersWithOrg.length
      ) {
        setIsDirty(true); // Indicates new subscribed users were added but not yet saved.
      }
      setOfficeWideShacklePermissionsGranted(true);
    }
  };

  return (
    <TableContainer>
      <Prompt
        when={isDirty}
        message="Are you sure you want to leave without saving?"
      />
      <TableHeader>
        <Text type="secondary">
          These users can assign this lockbox to a listing and release the
          shackle
        </Text>
        <ActionContainer>
          <Tooltip
            title={
              shouldDisableShacklePermissions()
                ? `Lockbox must${
                    lockbox.isUnclaimed ? ' be activated and' : ''
                  } have an owner or assignee before shackle permissions can be granted`
                : null
            }
          >
            <div>
              <Dropdown.Button
                menu={{
                  items: [
                    {
                      label: lockboxSlowAssignmentEnhancementsEnabled
                        ? 'Grant shackle permissions to all subscribed users'
                        : 'Add all subscribed users',
                      key: '1',
                    },
                  ],
                  disabled:
                    loading ||
                    (lockboxSlowAssignmentEnhancementsEnabled &&
                      disableBulkShacklePermissions),
                  onClick: () =>
                    Modal.confirm({
                      title: lockboxSlowAssignmentEnhancementsEnabled
                        ? `Grant shackle permissions to all subscribed users in ${permissionOffice}'s office?`
                        : 'Add all subscribed users?',
                      content: lockboxSlowAssignmentEnhancementsEnabled ? (
                        <>
                          <p>
                            All subscribed users in the
                            {` ${permissionOffice}'s `}
                            office will be granted shackle permissions for this
                            lockbox.
                          </p>
                          <p>
                            Users in the office who subscribe to their key in
                            the future, will automatically get shackle
                            permissions. Do you wish to continue?
                          </p>
                        </>
                      ) : (
                        'All subscribed users will be granted shackle permissions for this lockbox. Do you wish to continue?'
                      ),
                      okText: 'Assign',
                      cancelText: 'Cancel',
                      onOk: addAllSubscribedUsers,
                    }),
                }}
                onClick={() => {
                  setOpenModal(true);
                }}
                disabled={
                  shouldDisableShacklePermissions() ||
                  restrictLimitedUser ||
                  loading
                }
                data-cy="listing-settings-add-contact"
              >
                <StyledIcon type="plus" offset={[0, 1]} />
                Add User
              </Dropdown.Button>
            </div>
          </Tooltip>
          <Button
            type="primary"
            disabled={shouldDisableShacklePermissions() || restrictLimitedUser}
            onClick={saveShackPermission}
          >
            Save
          </Button>
        </ActionContainer>
      </TableHeader>

      <TableContent lg={lg}>
        <Table
          columns={shacklePermissionColumns}
          dataSource={permittedUsers}
          size="small"
          scroll={{ y: 240 }}
          rowSelection={
            !restrictLimitedUser
              ? {
                  type: 'checkbox',
                  onChange: keys => {
                    setUsersToUnassign(keys as string[]);
                  },
                  selectedRowKeys: usersToUnassign,
                  getCheckboxProps: user => ({
                    id: user.id,
                    disabled:
                      (lockbox.ownerType === 'user' &&
                        user.id === lockbox.owner) ||
                      (lockbox.assigneeType === 'user' &&
                        user.id === lockbox.assignee),
                  }),
                }
              : undefined
          }
          rowKey={'id'}
        />
      </TableContent>

      <UserSelectModal
        lockbox={lockbox}
        isDirty={isDirty}
        setIsDirty={setIsDirty}
        permittedUsers={permittedUsers}
        setPermittedUsers={setPermittedUsers}
        openModal={openModal}
        setOpenModal={setOpenModal}
        setOfficeWideShacklePermissionsGranted={
          setOfficeWideShacklePermissionsGranted
        }
      />

      {!restrictLimitedUser && (
        <ActionBar
          usersToUnassign={usersToUnassign}
          onClear={() => setUsersToUnassign([])}
          onUnassign={() => {
            setPermittedUsers(
              permittedUsers.filter(
                permittedUser =>
                  permittedUser.id === lockbox.owner ||
                  permittedUser.id === lockbox.assignee ||
                  !usersToUnassign.includes(permittedUser.id),
              ),
            );
            setUsersToUnassign([]);
            setIsDirty(true);
          }}
        />
      )}
    </TableContainer>
  );
};

export default ShacklePermissionTable;

const TableContainer = styled.div`
  width: 80%;
  margin: auto;
`;

const ActionContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 10px 5%;
  gap: 10px;
`;

const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TableContent = styled.div<{ lg?: boolean }>`
  width: 100%;
`;

const MLSNumberContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledIcon = styled(Icon)`
  && {
    color: inherit;
    transition: none;
  }
`;
